import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Shield, Search, Crown, Sparkles, UserCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const TIERS = ["Seeker", "Believer", "Disciple"];

/**
 * Admin-only panel rendered on /profile when the logged-in user has role==='admin'.
 * Lets an admin search any member by name/email, promote/demote admins, and change tier.
 * Hooked to GET /api/admin/members and PATCH /api/admin/members/:id.
 */
export default function AdminMembersPanel({ currentUserId }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/members", { params: search ? { q: search } : {} });
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  const setTier = async (m, tier) => {
    if (m.tier === tier) return;
    setBusyId(m.id);
    try {
      const { data } = await api.patch(`/admin/members/${m.id}`, { tier });
      setRows((rs) => rs.map((r) => (r.id === m.id ? { ...r, ...data } : r)));
      toast.success(`${data.name || data.email} is now a ${tier}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update tier");
    } finally {
      setBusyId(null);
    }
  };

  const toggleAdmin = async (m) => {
    const next = m.role === "admin" ? "member" : "admin";
    if (m.id === currentUserId && next === "member") {
      toast.error("You can't remove your own admin role. Promote a second admin first.");
      return;
    }
    if (!window.confirm(
      next === "admin"
        ? `Promote ${m.name || m.email} to co-admin? They will be able to manage all members.`
        : `Demote ${m.name || m.email} from admin?`
    )) return;
    setBusyId(m.id);
    try {
      const { data } = await api.patch(`/admin/members/${m.id}`, { role: next });
      setRows((rs) => rs.map((r) => (r.id === m.id ? { ...r, ...data } : r)));
      toast.success(next === "admin" ? "Promoted to admin" : "Demoted to member");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7" data-testid="admin-members-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2">
            <Shield className="h-3.5 w-3.5" /> Admin · Members
          </div>
          <h2 className="font-serif text-2xl text-jjblue mt-1">Manage who&rsquo;s who.</h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Promote a trusted member to co-admin or change a member&rsquo;s tier (free upgrades, gifts, or corrections).
          </p>
        </div>
      </div>

      <form onSubmit={onSearch} className="mt-5 flex gap-2 flex-wrap" data-testid="admin-members-search-form">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jj-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="input-jj !pl-10"
            data-testid="admin-members-search-input"
          />
        </div>
        <button type="submit" className="btn-primary !py-2 !px-5 text-sm" data-testid="admin-members-search-submit">
          Search
        </button>
        {q && (
          <button type="button" onClick={() => { setQ(""); load(""); }} className="btn-secondary !py-2 !px-4 text-sm" data-testid="admin-members-clear">
            Clear
          </button>
        )}
      </form>

      <div className="mt-5">
        {loading ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm">Loading members…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-[var(--jj-muted)] py-8 text-sm">No members matched.</div>
        ) : (
          <ul className="space-y-2" data-testid="admin-members-list">
            {rows.map((m) => (
              <li key={m.id} className="jj-card bg-white p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-member-${m.id}`}>
                <Avatar user={m} size={40} />
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/u/${m.id}`} className="font-medium text-jjblue hover:text-burgundy hover:underline truncate max-w-[14rem]" data-testid={`admin-member-name-${m.id}`}>
                      {m.name || "(no name)"}
                    </Link>
                    {m.role === "admin" && (
                      <span className="text-[10px] uppercase tracking-wider bg-burgundy text-cream px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Crown className="h-3 w-3" /> admin
                      </span>
                    )}
                    {m.id === currentUserId && (
                      <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-burgundy px-2 py-0.5 rounded-full">you</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--jj-muted)] truncate max-w-[24rem]">{m.email}</div>
                </div>

                {/* Tier selector */}
                <div className="relative" data-testid={`admin-member-tier-${m.id}`}>
                  <select
                    value={m.tier}
                    onChange={(e) => setTier(m, e.target.value)}
                    disabled={busyId === m.id}
                    className="appearance-none input-jj !py-1.5 !pl-3 !pr-8 text-xs cursor-pointer"
                    aria-label="Tier"
                    data-testid={`admin-member-tier-select-${m.id}`}
                  >
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="h-3.5 w-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--jj-muted)] pointer-events-none" />
                </div>

                <button
                  onClick={() => toggleAdmin(m)}
                  disabled={busyId === m.id}
                  className={`text-xs !py-1.5 !px-3 inline-flex items-center gap-1 ${m.role === "admin" ? "btn-secondary" : "btn-gold"}`}
                  data-testid={`admin-member-toggle-${m.id}`}
                >
                  {m.role === "admin" ? <UserCheck className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {m.role === "admin" ? "Demote" : "Make admin"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
